import React, { useState, useMemo } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton, LoadingSpinner } from '@/components';
import { WebConfirmModal } from '@/components/common/modals';
import { useToast } from '@/contexts/admin/ToastContext';
import { PaymentModal, PaymentModalContent, PaymentMethodCard } from '@/components/residents/payments';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { usePaymentMethodSelection } from '@/features/payments/hooks/usePaymentValidation';
import { 
  getPaymentMethodLabel, 
  hasPaymentMethodCardExpired,
} from '@/utils/residents/paymentUtils';
import { 
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
} from '@/store/api';

type Params = {
  leaseId: number;
  residentId?: number;
  selectedMethodId?: string | number;
  returnTo?: string;
  successPage?: string;
  addOrChangePaymentMethodPage?: string;
};

export const ManageMyWalletModal: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { showSuccess, showError } = useToast();
  const { 
    leaseId, 
    residentId, 
    selectedMethodId, 
    returnTo, 
    successPage,
    addOrChangePaymentMethodPage 
  } = (route.params || {}) as Params;

  // Get payment data
  const {
    paymentMethods,
    selectedPaymentMethod,
    isLoading,
    updateSelectedPaymentMethod,
    refreshPaymentMethods,
  } = usePaymentData(residentId);

  // Payment method selection logic
  const methodSelection = usePaymentMethodSelection(paymentMethods, selectedMethodId);
  const {
    availableMethods,
    expiredMethods,
    blockedMethods,
    hasValidMethods,
    allMethodsExpired,
  } = methodSelection;

  // Local state
  const [localSelectedMethod, setLocalSelectedMethod] = useState(
    selectedPaymentMethod || methodSelection.selectedMethod
  );

  // API mutations
  const [putBankAccount, { isLoading: isUpdatingBank }] = usePutBankAccountMutation();
  const [putCreditCard, { isLoading: isUpdatingCard }] = usePutCreditCardMutation();
  const [deleteBankAccount, { isLoading: isDeletingBank }] = useDeleteBankAccountMutation();
  const [deleteCreditCard, { isLoading: isDeletingCard }] = useDeleteCreditCardMutation();
  
  const isUpdating = isUpdatingBank || isUpdatingCard;
  const isDeleting = isDeletingBank || isDeletingCard;

  // Delete confirmation modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<any>(null);

  const hasNoMethods = paymentMethods.length === 0;
  const hasOnlyOneMethod = paymentMethods.length === 1;

  // Handle method selection
  const handleSelectMethod = (method: any) => {
    setLocalSelectedMethod(method);
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (localSelectedMethod) {
      updateSelectedPaymentMethod(localSelectedMethod);
      
      if (successPage) {
        navigation.replace(successPage, { leaseId });
      } else if (returnTo) {
        navigation.replace(returnTo, { leaseId });
      } else {
        navigation.goBack();
      }
    }
  };

  // Handle add new payment method
  const handleAddPaymentMethod = () => {
    // Replace current modal with add payment method modal
    if (addOrChangePaymentMethodPage) {
      navigation.replace(addOrChangePaymentMethodPage, { 
        leaseId,
        returnTo: 'ManageMyWallet'
      });
    } else {
      navigation.replace('AddOrChangePaymentMethod', { 
        leaseId,
        returnTo: 'ManageMyWallet'
      });
    }
  };

  // Handle set as default
  const handleSetAsDefault = async (method: any) => {
    try {
      if (method.type === 'bankAccount' || method.accttype === 'ECHK') {
        await putBankAccount({
          sourceId: method.sourceId || method.id,
          bankAccount: { isDefault: true },
        }).unwrap();
      } else {
        await putCreditCard({
          sourceId: method.sourceId || method.id,
          creditCard: { isDefault: true },
        }).unwrap();
      }
      
      // Show success toast
      showSuccess('Payment method set as default successfully');
      
      // Refresh the payment methods data (automatic via cache invalidation + manual backup)
      refreshPaymentMethods();
    } catch (error) {
      console.error('Set as default error:', error);
      showError('Failed to set payment method as default. Please try again.');
    }
  };

  // Handle delete payment method
  const handleDeletePaymentMethod = (method: any) => {
    setMethodToDelete(method);
    setDeleteModalVisible(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!methodToDelete) return;

    try {
      let result;
      const sourceId = methodToDelete.sourceId || methodToDelete.id;
      const isBank = methodToDelete.type === 'bankAccount' || methodToDelete.accttype === 'ECHK';
      
      if (isBank) {
        result = await deleteBankAccount({ sourceId: String(sourceId) }).unwrap();
      } else {
        result = await deleteCreditCard({ sourceId: String(sourceId) }).unwrap();
      }

      if (result.success) {
        // Refresh payment methods (automatic via cache invalidation + manual backup)
        refreshPaymentMethods();
        setDeleteModalVisible(false);
        setMethodToDelete(null);
        
        // Show success toast
        showSuccess('Payment method removed successfully');
      } else {
        showError('Failed to delete payment method. Please try again.');
      }
    } catch (error) {
      console.error('Delete payment method error:', error);
      showError('Failed to delete payment method. Please try again.');
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setMethodToDelete(null);
  };

  const onClose = () => navigation.goBack();

  const hasChanges = localSelectedMethod?.id !== selectedPaymentMethod?.id;

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <CardOutlineIcon />
      <Text style={styles.emptyTitle}>No saved payment methods</Text>
      <Text style={styles.emptyMessage}>
        You'll need to add a new payment method before making your next payment.
      </Text>
    </View>
  );

  const renderPaymentMethods = () => {
    // Combine all methods for the grid layout (matching web behavior)
    const sortedMethods = [...paymentMethods]
      .sort(({ isDefault: x = false }, { isDefault: y = false }) => Number(y) - Number(x));

    return (
      <View style={[
        styles.methodsGrid,
        hasOnlyOneMethod && styles.methodsGridSingle,
      ]}>
        {sortedMethods.map((method, index) => (
          <View
            key={method.id || method.sourceId}
            style={[styles.methodItem, hasOnlyOneMethod && styles.methodItemSingle]}
          >
            <PaymentMethodCard
              method={method}
              index={index}
              isOnlyCard={hasOnlyOneMethod}
              isExpired={hasPaymentMethodCardExpired(method.expiry)}
              onSetAsDefault={() => handleSetAsDefault(method)}
              onDelete={() => handleDeletePaymentMethod(method)}
              isUpdating={isUpdating}
              canDelete={!method.isDefault || hasOnlyOneMethod}
              hasAutoPay={false} // TODO: Check if method has autopay
              showActions={true}
            />
          </View>
        ))}
      </View>
    );
  };

  const getFooterButtons = () => {
    if (hasNoMethods) {
      return {
        backTitle: "Do it later",
        backVariant: "link" as const,
        nextTitle: "Add Now",
        nextAction: handleAddPaymentMethod,
      };
    }
    
    return {
      backTitle: "",
      backVariant: "outlined" as const,
      nextTitle: "Done",
      nextAction: handleSaveChanges,
    };
  };

  const footerConfig = getFooterButtons();

  return (
    <>
      <PaymentModal title="Manage my wallet" onClose={onClose} width={700}>
        <PaymentModalContent>
          <View style={styles.modalContent}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <LoadingSpinner />
              </View>
            ) : hasNoMethods ? (
              renderEmptyState()
            ) : (
              renderPaymentMethods()
            )}
          </View>
        </PaymentModalContent>
        
        <View style={[styles.footer, hasNoMethods && styles.footerSpaceBetween]}>
          {footerConfig.backTitle && (
            <RNButton 
              title={footerConfig.backTitle} 
              variant={footerConfig.backVariant} 
              onPress={onClose}
              style={styles.footerButton}
              textStyle={styles.footerButtonText}
            />
          )}
          <RNButton 
            title={footerConfig.nextTitle} 
            variant="primary" 
            onPress={footerConfig.nextAction}
            disabled={false}
            style={hasNoMethods ? styles.footerButton : styles.doneButton}
            textStyle={styles.footerButtonText}
          />
        </View>
      </PaymentModal>

      {/* Delete Confirmation Modal */}
      <WebConfirmModal
        visible={deleteModalVisible}
        title="Remove payment method"
        message={`Are you sure you want to remove ${methodToDelete ? getPaymentMethodLabel(methodToDelete) : 'this payment method'}?`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};


// Helper Icons
const CardOutlineIcon: React.FC = () => {
  // Use the credit card icon as a fallback for the card outline
  const Icon = RESIDENTS_SVG_COMPONENTS.CREDIT_CARD;
  return (
    <View style={styles.emptyIconContainer}>
      <Icon width={64} height={64} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />
    </View>
  );
};

const styles = StyleSheet.create({
  // Modal Content
  modalContent: {
    padding: 32, // Matches web's p-8 (32px)
    minHeight: 400,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
  },

  // Empty State
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyIconContainer: {
    marginBottom: 24,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
    textAlign: 'center',
  },
  emptyMessage: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 16,
    maxWidth: 280,
  },

  // Payment Methods Grid
  methodsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  methodsGridSingle: {
    flexDirection: 'column',
  },
  methodItem: {
    width: '48%',
    marginBottom: 16,
  },
  methodItemSingle: {
    width: '100%',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    gap: 12,
  },
  footerSpaceBetween: {
    justifyContent: 'space-between',
  },
  footerButton: {
    minWidth: 120,
    ...(Platform.OS === 'web' && { flex: 0 }),
  },
  footerButtonText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  doneButton: {
    minWidth: 120,
  },
});

export default ManageMyWalletModal;
